import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import ValidInputError from './ValidInputError';
import ValidInputLabel from './ValidInputLabel';
import { isValidDateString, isDateInRange, generateCalendarDays, DayObject } from './ValidInputDate.utils';
import CalendarPopup from './CalendarPopup';

// TYPE
export type ValidPropDate = {
    key: string;
    lbl?: string;
    val: string;
    err: string;
    min?: string;
    max?: string;
};

// VIEW MODEL
export function validPropDateVM(key: string, lbl?: string, val?: string, min?: string, max?: string): ValidPropDate {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val: val && isValidDateString(val) ? val : '',
        err: '',
        min: min,
        max: max,
    };
}

interface ValidInputDateProps {
    prop: ValidPropDate;
    onChange?: (key: string, value: string) => void;
    id?: string;
    disabled?: boolean;
    alternateLabel?: string;
}

const ValidInputDate: React.FC<ValidInputDateProps> = ({ prop, onChange, id, disabled, alternateLabel }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [displayMonth, setDisplayMonth] = useState<Date>(new Date());
    // Local input state for editing
    const [inputValue, setInputValue] = useState<string>(formatDateForInputDisplay(prop.val));
    const [localError, setLocalError] = useState<string>('');

    // Initialize display month from selected date if available
    useEffect(() => {
        if (isValidDateString(prop.val)) {
            const [year, month, day] = prop.val.split('-').map(Number);
            // Only update displayMonth if not already showing this month (prevents calendar jumping on every keystroke)
            if (displayMonth.getFullYear() !== year || displayMonth.getMonth() !== month - 1) {
                setDisplayMonth(new Date(year, month - 1, day));
            }
        }
        // Only run when prop.val changes, not displayMonth
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prop.val]);

    // Sync inputValue with prop.val when not editing (i.e., when prop.val changes externally)
    useEffect(() => {
        // Only update if not currently editing (i.e., input is not focused)
        if (!isOpen && document.activeElement !== document.getElementById(id || '')) {
            setInputValue(formatDateForInputDisplay(prop.val));
            setLocalError('');
        }
    }, [prop.val, isOpen, id]);

    // Close calendar on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return (): void => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Handle keyboard events
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!isOpen) {
                if (e.key === 'ArrowDown' || e.key === 'Enter') {
                    e.preventDefault();
                    setIsOpen(true);
                }
                return;
            }

            switch (e.key) {
                case 'Escape':
                    e.preventDefault();
                    setIsOpen(false);
                    break;
                case 'PageUp':
                    e.preventDefault();
                    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1));
                    break;
                case 'PageDown':
                    e.preventDefault();
                    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1));
                    break;
                default:
                    break;
            }
        },
        [isOpen, displayMonth],
    );

    // Handle day selection
    const handleDayClick = useCallback(
        (dayObj: DayObject) => {
            if (dayObj.isDisabled) {
                return;
            }

            const year = dayObj.date.getFullYear();
            const month = dayObj.date.getMonth() + 1;
            const day = dayObj.date.getDate();

            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            if (onChange) {
                onChange(prop.key, dateStr);
            }

            setIsOpen(false);
        },
        [onChange, prop.key],
    );

    // Handle month navigation
    const handlePrevMonth = useCallback(() => {
        const newMonth = new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1);
        setDisplayMonth(newMonth);
    }, [displayMonth]);

    const handleNextMonth = useCallback(() => {
        const newMonth = new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1);
        setDisplayMonth(newMonth);
    }, [displayMonth]);

    // Check if navigation buttons should be disabled
    const canGoPrev = useMemo(() => {
        if (!prop.min) {
            return true;
        }
        const [minYear, minMonth] = prop.min.split('-').map(Number);
        const minDate = new Date(minYear, minMonth - 1, 1);
        const currentMonthStart = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), 1);
        const canGo = currentMonthStart > minDate;
        return canGo;
    }, [displayMonth, prop.min]);

    const canGoNext = useMemo(() => {
        if (!prop.max) {
            return true;
        }
        const [maxYear, maxMonth] = prop.max.split('-').map(Number);
        const maxDate = new Date(maxYear, maxMonth - 1, 1);
        const nextMonthStart = new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1);
        const canGo = nextMonthStart <= maxDate;
        return canGo;
    }, [displayMonth, prop.max]);

    // Generate calendar weeks
    const calendarWeeks = useMemo(
        () => generateCalendarDays(displayMonth, prop.val, prop.min, prop.max),
        [displayMonth, prop.val, prop.min, prop.max],
    );

    // Validate date if value changed
    const hasError = useMemo(() => {
        if (!prop.val) return false;
        if (!isValidDateString(prop.val)) return true;
        if (!isDateInRange(prop.val, prop.min, prop.max)) return true;
        return !!prop.err;
    }, [prop.val, prop.min, prop.max, prop.err]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(e => {
        // Accept any input, allow user to type slashes or dashes, and do not auto-insert them
        // Only allow numbers and slashes/dashes
        let val = e.target.value.replace(/[^0-9/\-]/g, '');
        // Optionally, collapse multiple slashes/dashes
        val = val.replace(/[\/\-]{2,}/g, '/');
        setInputValue(val);
    }, []);

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
        e => {
            const val = e.target.value.trim();
            // Try M/D/YYYY or MM/DD/YYYY or M-D-YYYY or MM-DD-YYYY
            const mmddyyyy = val.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
            const yyyymmdd = val.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
            let normalized = val;
            if (mmddyyyy) {
                // Pad month and day to 2 digits
                const month = mmddyyyy[1].padStart(2, '0');
                const day = mmddyyyy[2].padStart(2, '0');
                normalized = `${mmddyyyy[3]}-${month}-${day}`;
            } else if (yyyymmdd) {
                const month = yyyymmdd[2].padStart(2, '0');
                const day = yyyymmdd[3].padStart(2, '0');
                normalized = `${yyyymmdd[1]}-${month}-${day}`;
            }

            // Check for valid date string
            if (isValidDateString(normalized)) {
                if (onChange) onChange(prop.key, normalized);
                setInputValue(formatDateForInputDisplay(normalized));
                setLocalError('');
            } else {
                // If invalid, keep the user's input as-is and show a specific error
                setInputValue(val);
                // Show format error
                setLocalError(`Invalid ${prop.lbl?.toLowerCase()}.`);
            }
        },
        [onChange, prop.key, prop.lbl],
    );

    return (
        <div className="component-valid-input input-date">
            <div className="input-wrapper">
                {prop.lbl ? (
                    <ValidInputLabel
                        label={alternateLabel ?? prop.lbl}
                        noMinWidth
                    />
                ) : null}

                <div
                    className={`valid-input input-date-wrapper${isOpen ? ' open' : ''}`}
                    ref={containerRef}
                >
                    <input
                        type="text"
                        id={id}
                        className={`valid-input-date-input ${hasError ? 'error' : ''}`}
                        value={inputValue}
                        min={prop.min}
                        max={prop.max}
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={disabled}
                        aria-label={`${prop.lbl || prop.key} date input`}
                        aria-expanded={isOpen}
                        aria-haspopup="dialog"
                        placeholder="MM/DD/YYYY"
                        autoComplete="off"
                    />
                    <span
                        className="calendar-icon"
                        aria-hidden="true"
                        onClick={() => !disabled && setIsOpen(true)}
                        style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    >
                        <FontAwesomeIcon icon={faCalendar} />
                    </span>

                    {isOpen && !disabled && (
                        <CalendarPopup
                            displayMonth={displayMonth}
                            onPrevMonth={handlePrevMonth}
                            onNextMonth={handleNextMonth}
                            canGoPrev={canGoPrev}
                            canGoNext={canGoNext}
                            calendarWeeks={calendarWeeks}
                            onDayClick={handleDayClick}
                        />
                    )}
                </div>
            </div>

            <ValidInputError
                err={prop.err || localError || (hasError ? 'Invalid date' : '')}
                showMsg={true}
            />
        </div>
    );
};

export default ValidInputDate;

// Format YYYY-MM-DD to MM/DD/YYYY for input display
function formatDateForInputDisplay(val?: string): string {
    if (val && isValidDateString(val)) {
        const [y, m, d] = val.split('-');
        // Remove leading zeros for month and day
        const month = String(Number(m));
        const day = String(Number(d));
        return `${month}/${day}/${y}`;
    }
    return val || '';
}
